import { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Playlist } from '@phosphor-icons/react';

export default function AddPlaylistDialog({
  open,
  onOpenChange,
  onSaved,
}) {
  const [link, setLink] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setLink('');
    setSaving(false);
  };

  const handleOpenChange = value => {
    if (!value) reset();
    onOpenChange?.(value);
  };

  const addPlaylist = async () => {
    const url = link.trim();

    if (!url) {
      toast.error('Paste a Spotify playlist URL');
      return;
    }

    const lower = url.toLowerCase();

    const isSpotify =
      lower.includes('open.spotify.com/playlist/');

    const isYouTube =
      lower.includes('youtube.com/playlist') ||
      (lower.includes('youtube.com/watch') && lower.includes('list='));

    if (!isSpotify && !isYouTube) {
      toast.error('Please use a Spotify or YouTube playlist URL');
      return;
    }

    setSaving(true);

    try {
      const { data } = await api.post('/playlists/import', {
        url,
      });

      toast.success(`Playlist "${data.name}" added`);

      onSaved?.(data);

      reset();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not add playlist'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">
            Add a playlist
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Playlist
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />

            <Input
              data-testid="playlist-link-input"
              placeholder="Paste a Spotify or YouTube playlist URL…"
              value={link}
              onChange={e => setLink(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !saving) {
                  addPlaylist();
                }
              }}
              className="pl-9 bg-zinc-950 border-zinc-800"
            />
          </div>

          <div className="text-[11px] text-zinc-500">
            Spotify: https://open.spotify.com/playlist/… · YouTube: https://youtube.com/playlist?list=…
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            className="border-zinc-800 hover:border-zinc-600"
          >
            Cancel
          </Button>

          <Button
            data-testid="playlist-add-btn"
            onClick={addPlaylist}
            disabled={saving || !link.trim()}
            className="btn-neon rounded-full"
          >
            {saving ? 'Adding…' : 'Add playlist'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
